import {
  KnowledgeBaseAdherenceCorrectness,
  type GradingKnowledgeBaseAdherenceStatement,
} from "@repo/app-types/entities/knowledgeBaseAdherence";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type BasicTeammate,
} from "@repo/app-types/entities/teammate";
import {
  getGradingDisplayNames,
  type GradingId,
  type TeammateAccountGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Link } from "@repo/ui/Link/index";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconCopy, IconLink } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { twMerge } from "tailwind-merge";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { copyRichText } from "../../helpers/utilities.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";

interface ShareKnowledgeBaseAdherenceFindingModalProps {
  isOpen: boolean;
  onClose: () => void;
  grading: TeammateAccountGrading;
  statement: GradingKnowledgeBaseAdherenceStatement;
  statementId: string;
}

export const ShareKnowledgeBaseAdherenceFindingModal: FC<
  ShareKnowledgeBaseAdherenceFindingModalProps
> = ({ isOpen, onClose, grading, statement, statementId }) => {
  const dispatch = useAppDispatch();
  const { data: teammate } = useBasicTeammateQuery(grading.teammate_id);
  const { primaryDisplayName } = getGradingDisplayNames(grading);

  const link = getShareLinkForKnowledgeBaseAdherenceFinding({
    baseUrl: window.location.origin,
    playbookId: grading.playbook_id,
    gradingId: grading.id,
    statementId,
  });

  const shareSnippet = generateShareSnippet({
    primaryDisplayName,
    statement,
    link,
    teammate,
    lastRecordedAt: grading.last_recorded_at,
  });

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader isCloseVisible>Share finding</ModalHeader>

      <div
        className={twMerge(
          "mt-4 flex w-full cursor-text space-x-2 rounded-md border border-solid border-border-strong bg-slate-50 px-2 py-2 text-xs hover:bg-slate-100",
          PH_MASK_CLASS,
        )}
      >
        {shareSnippet.jsx}
      </div>

      <div className="mt-2 text-sm text-muted-foreground">
        <span>Paste this snippet into Slack, Teams, or emails to share.</span>
      </div>

      <div className="mt-4 flex items-center space-x-4">
        <Button
          variant={ButtonVariant.Primary}
          onClick={async () => {
            await copyRichText(shareSnippet.richText, shareSnippet.plainText);

            showNotification(dispatch, {
              id: `kb-adherence-${grading.id}-${statementId}-copied-${new Date().getTime()}`,
              primaryMessage: "Share text copied",
              type: NotificationType.SUCCESS,
            });
          }}
          icon={<IconCopy className="h-4 w-4" />}
        >
          Copy text
        </Button>
        <Button
          variant={ButtonVariant.Secondary}
          onClick={async () => {
            await navigator.clipboard.writeText(link);

            showNotification(dispatch, {
              id: `kb-adherence-${grading.id}-${statementId}-link-copied-${new Date().getTime()}`,
              primaryMessage: "Link copied",
              type: NotificationType.SUCCESS,
            });
          }}
          icon={<IconLink className="h-4 w-4" />}
        >
          Copy link
        </Button>
      </div>
    </Modal>
  );
};

function getShareLinkForKnowledgeBaseAdherenceFinding({
  baseUrl,
  playbookId,
  gradingId,
  statementId,
}: {
  baseUrl: string;
  playbookId: PlaybookId;
  gradingId: GradingId;
  statementId: string;
}) {
  return `${baseUrl}/performance/playbooks/${encodeURIComponent(playbookId)}/customer-interactions/${encodeURIComponent(gradingId)}/assessment/knowledge-base-adherence/${encodeURIComponent(statementId)}`;
}

function generateShareSnippet({
  primaryDisplayName,
  statement,
  link,
  teammate,
  lastRecordedAt,
}: {
  primaryDisplayName: string | undefined;
  statement: GradingKnowledgeBaseAdherenceStatement;
  link: string;
  teammate: BasicTeammate | undefined;
  lastRecordedAt: string;
}) {
  const linkElement = <Link href={link}>See the full finding</Link>;
  const teammateName = teammate
    ? buildTeammateDisplayNames(teammate).primaryDisplayName
    : "Unknown";
  const recordedDate = DateTime.fromISO(lastRecordedAt).toFormat("MMM d, yyyy");
  const correctness = renderCorrectnessLabel(statement.correctness);
  const firstQuote = statement.quotes.at(0)?.text;

  const plainTextContent = [
    `Knowledge base adherence finding for ${teammateName}`,
    `Customer: ${primaryDisplayName || "Unknown"} (${recordedDate})`,
    `Status: ${correctness}`,
    `Summary: ${statement.summary}`,
    firstQuote ? `Call quote: "${firstQuote}"` : null,
    "",
    link,
  ]
    .filter(Boolean)
    .join("\n");

  const jsx = (
    <div>
      <strong>Knowledge base adherence finding for {teammateName}</strong>
      <br />
      <strong>Customer:</strong> {primaryDisplayName || "Unknown"} (
      {recordedDate})
      <br />
      <strong>Status:</strong> {correctness}
      <br />
      <strong>Summary:</strong> {statement.summary}
      <br />
      {firstQuote ? (
        <>
          <strong>Call quote:</strong> &quot;{firstQuote}&quot;
          <br />
        </>
      ) : null}
      {linkElement}
    </div>
  );

  return {
    plainText: plainTextContent,
    richText: renderToStaticMarkup(jsx),
    jsx,
  };
}

function renderCorrectnessLabel(
  correctness: GradingKnowledgeBaseAdherenceStatement["correctness"],
) {
  switch (correctness) {
    case KnowledgeBaseAdherenceCorrectness.Correct:
      return "Correct";
    case KnowledgeBaseAdherenceCorrectness.Incorrect:
      return "Inaccuracy";
    case KnowledgeBaseAdherenceCorrectness.Unknown:
      return "Unverifiable";
  }
}
